import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Video, PlugZap, Check, X, Mail, Send, SlidersHorizontal, KeyRound, Download } from "lucide-react";
import { toast } from "sonner";

export default function ImpostazioniPage() {
  const { user } = useAuth();
  const [serverUrl, setServerUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [secretSet, setSecretSet] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [bbbSpec, setBbbSpec] = useState([]);
  const [bbbOpts, setBbbOpts] = useState({});
  const [poolMax, setPoolMax] = useState(150);
  const [resetTxt, setResetTxt] = useState(""); const [resetting, setResetting] = useState(false); const [resetResult, setResetResult] = useState(null);
  const [fromName, setFromName] = useState("");
  const [firma, setFirma] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [inattivita, setInattivita] = useState(15);
  const [savingInatt, setSavingInatt] = useState(false);

  useEffect(() => {
    api.get("/settings/bbb").then(r => {
      setServerUrl(r.data.server_url || ""); setSecretSet(r.data.secret_set);
      setBbbSpec(r.data.opzioni_spec || []); setBbbOpts(r.data.opzioni || {});
      setPoolMax(r.data.pool_max || 150);
    }).catch(()=>{});
    api.get("/settings/email").then(r => { setFromName(r.data.from_name || ""); setFirma(r.data.firma || ""); setReplyTo(r.data.reply_to || ""); }).catch(()=>{});
    api.get("/settings/inattivita").then(r => setInattivita(r.data.inactivity_min || 15)).catch(()=>{});
  }, []);

  const setOpt = (k, v) => setBbbOpts(o => ({ ...o, [k]: v }));

  const save = async () => {
    try {
      await api.put("/settings/bbb", { server_url: serverUrl, opzioni: bbbOpts, pool_max: poolMax, ...(secret ? { secret } : {}) });
      toast.success("Configurazione BBB salvata");
      setSecret(""); setSecretSet(true); setTestResult(null);
    } catch { toast.error("Errore salvataggio"); }
  };
  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/settings/bbb/test");
      setTestResult(data);
    } catch (e) { setTestResult({ ok: false, error: e.response?.data?.detail || "Errore" }); }
    finally { setTesting(false); }
  };

  const saveEmail = async () => {
    try {
      await api.put("/settings/email", { from_name: fromName, firma, reply_to: replyTo });
      toast.success("Configurazione email salvata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const testEmail = async () => {
    setTestSending(true);
    try {
      await api.post("/settings/email/test");
      toast.success("Email di prova inviata al tuo indirizzo");
    } catch (e) { toast.error(e.response?.data?.detail || "Invio fallito"); }
    finally { setTestSending(false); }
  };
  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-database", { conferma: true });
      setResetResult(data); setResetTxt("");
      toast.success("Database svuotato. Nuovo admin creato.");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore reset database"); }
    finally { setResetting(false); }
  };
  const scaricaResetPdf = async () => {
    try {
      const r = await api.post("/users/credenziali-pdf", { nome: resetResult.nome, email: resetResult.email, password: resetResult.password }, { responseType: "blob" });
      const link = document.createElement("a"); link.href = URL.createObjectURL(r.data); link.download = `credenziali_${resetResult.email}.pdf`; link.click();
    } catch { toast.error("Errore download PDF"); }
  };
  const escLogout = () => { localStorage.removeItem("isteform_token"); window.location.href = "/"; };

  return (
    <div className="p-4 sm:p-8 max-w-3xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Configurazione Piattaforma</div>
        <h1 className="text-3xl font-bold text-stone-900">Impostazioni</h1>
      </div>

      <Card className="p-6 bg-white border-[#E2E8F0]" data-testid="bbb-settings-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg gradient-terracotta flex items-center justify-center text-white"><Video className="w-5 h-5"/></div>
          <div>
            <div className="font-bold text-stone-900">Server BigBlueButton</div>
            <div className="text-sm text-stone-600">Le lezioni online creano automaticamente la stanza sul tuo server BBB</div>
          </div>
        </div>
        <div className="grid gap-4">
          <div>
            <Label>URL server BBB</Label>
            <Input data-testid="bbb-url-input" value={serverUrl} onChange={e=>setServerUrl(e.target.value)} placeholder="https://bbb.isteform.it"/>
            <div className="text-xs text-stone-500 mt-1">Senza /bigbluebutton/api — lo aggiungiamo noi</div>
          </div>
          <div>
            <Label>Shared secret {secretSet && <span className="text-emerald-700 text-xs">(già configurato — lascia vuoto per mantenerlo)</span>}</Label>
            <Input data-testid="bbb-secret-input" type="password" value={secret} onChange={e=>setSecret(e.target.value)} placeholder={secretSet ? "••••••••••••" : "Incolla il shared secret del server"}/>
          </div>
          <div>
            <Label>Posti totali del piano bbbserver</Label>
            <Input data-testid="bbb-pool-max-input" type="number" min="1" value={poolMax} onChange={e=>setPoolMax(e.target.value === "" ? "" : Number(e.target.value))} className="max-w-[180px]"/>
            <div className="text-xs text-stone-500 mt-1">Connessioni simultanee incluse nel tuo piano. Usato per l'indicatore di capienza nel Calendario. Alzalo/abbassalo se cambi piano.</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="bbb-save-btn">Salva configurazione</Button>
            <Button variant="outline" onClick={test} disabled={testing || !secretSet} data-testid="bbb-test-btn"><PlugZap className="w-4 h-4 mr-2"/>{testing ? "Test..." : "Testa connessione"}</Button>
          </div>
          {testResult && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="bbb-test-result">
              {testResult.ok ? <><Check className="w-4 h-4"/> Server raggiungibile (HTTP {testResult.status})</> : <><X className="w-4 h-4"/> {testResult.error || "Server non raggiungibile"}</>}
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-xs text-stone-500">
          Quando il server è configurato, le lezioni create dal calendario generano automaticamente la stanza BBB con registrazione attiva. Se il server non risponde, la lezione viene creata senza link.
        </div>
      </Card>

      <Card className="p-6 bg-white border-[#E2E8F0] mt-6" data-testid="bbb-params-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg gradient-terracotta flex items-center justify-center text-white"><SlidersHorizontal className="w-5 h-5"/></div>
          <div>
            <div className="font-bold text-stone-900">Parametri aula virtuale (BigBlueButton)</div>
            <div className="text-sm text-stone-600">Tutte le opzioni applicate ad ogni nuova lezione online creata dal calendario</div>
          </div>
        </div>
        <div className="space-y-6">
          {[...new Set(bbbSpec.map(s => s.group))].map(group => (
            <div key={group}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#1F4E79] mb-3">{group}</div>
              <div className="grid gap-4">
                {bbbSpec.filter(s => s.group === group).map(sp => {
                  const val = bbbOpts[sp.key];
                  if (sp.type === "bool") return (
                    <div key={sp.key} className="flex items-start justify-between gap-4">
                      <div>
                        <Label className="cursor-pointer">{sp.label}</Label>
                        {sp.help && <div className="text-xs text-stone-500 mt-0.5">{sp.help}</div>}
                      </div>
                      <Switch data-testid={`bbb-opt-${sp.key}`} checked={!!val} onCheckedChange={v => setOpt(sp.key, v)} />
                    </div>
                  );
                  if (sp.type === "int") return (
                    <div key={sp.key}>
                      <Label>{sp.label}</Label>
                      <Input data-testid={`bbb-opt-${sp.key}`} type="number" value={val ?? 0} onChange={e => setOpt(sp.key, e.target.value === "" ? 0 : Number(e.target.value))} className="max-w-[180px]"/>
                      {sp.help && <div className="text-xs text-stone-500 mt-1">{sp.help}</div>}
                    </div>
                  );
                  if (sp.type === "select") return (
                    <div key={sp.key}>
                      <Label>{sp.label}</Label>
                      <Select value={val} onValueChange={v => setOpt(sp.key, v)}>
                        <SelectTrigger data-testid={`bbb-opt-${sp.key}`} className="max-w-[280px]"><SelectValue/></SelectTrigger>
                        <SelectContent>{sp.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                      {sp.help && <div className="text-xs text-stone-500 mt-1">{sp.help}</div>}
                    </div>
                  );
                  return (
                    <div key={sp.key}>
                      <Label>{sp.label}</Label>
                      <Input data-testid={`bbb-opt-${sp.key}`} value={val ?? ""} onChange={e => setOpt(sp.key, e.target.value)} placeholder="(vuoto = non impostato)"/>
                      {sp.help && <div className="text-xs text-stone-500 mt-1">{sp.help}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="bbb-params-save-btn">Salva parametri aula</Button>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-xs text-stone-500">
          I parametri valgono per le lezioni create <b>dopo</b> il salvataggio. Alcuni valori (es. numero massimo partecipanti) dipendono dai limiti del piano del provider BBB.
        </div>
      </Card>

      <Card className="p-6 bg-white border-[#E2E8F0] mt-6" data-testid="email-settings-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg gradient-terracotta flex items-center justify-center text-white"><Mail className="w-5 h-5"/></div>
          <div>
            <div className="font-bold text-stone-900">Email dal portale</div>
            <div className="text-sm text-stone-600">Personalizza nome mittente e firma delle email inviate (credenziali, lezioni, promemoria, solleciti)</div>
          </div>
        </div>
        <div className="grid gap-4">
          <div>
            <Label>Nome mittente</Label>
            <Input data-testid="email-fromname-input" value={fromName} onChange={e=>setFromName(e.target.value)} placeholder="Segreteria ISTEFORM"/>
          </div>
          <div>
            <Label>Firma (aggiunta in coda a ogni email)</Label>
            <Textarea data-testid="email-firma-input" rows={3} value={firma} onChange={e=>setFirma(e.target.value)} placeholder={"Segreteria Centro Studi ISTEFORM\nTel. ... · email@isteform.it"}/>
          </div>
          <div>
            <Label>Email per le risposte (Reply-To)</Label>
            <Input data-testid="email-replyto-input" type="email" value={replyTo} onChange={e=>setReplyTo(e.target.value)} placeholder="segreteria@tuodominio.it"/>
            <div className="text-xs text-stone-500 mt-1">Le risposte degli studenti alle email del portale arriveranno a questo indirizzo del tuo dominio.</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveEmail} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="email-save-btn">Salva configurazione</Button>
            <Button variant="outline" onClick={testEmail} disabled={testSending} data-testid="email-test-btn"><Send className="w-4 h-4 mr-2"/>{testSending ? "Invio..." : "Invia email di prova"}</Button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-xs text-stone-500">
          L'indirizzo di invio resta quello del servizio email integrato; qui personalizzi il nome visualizzato e la firma.
        </div>
      </Card>

      <Card className="p-6 bg-white border-2 border-red-200 mt-6" data-testid="reset-db-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center text-white"><X className="w-5 h-5"/></div>
          <div>
            <div className="font-bold text-red-700">Zona pericolosa — Reset dati</div>
            <div className="text-sm text-stone-600">Svuota tutte le tabelle (anagrafiche, classi, lezioni, pagamenti, presenze, materiali, notifiche…) mantenendo la struttura. Le impostazioni (server BBB, email) restano. Verrà lasciato un solo admin: info@isteform.com.</div>
          </div>
        </div>
        {!resetResult ? (
          <div className="grid gap-3 max-w-md">
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">⚠️ Operazione irreversibile. Per confermare digita <b>RESET</b> qui sotto.</div>
            <Input value={resetTxt} onChange={e=>setResetTxt(e.target.value)} placeholder='Digita RESET per abilitare' data-testid="reset-confirm-input"/>
            <Button onClick={doReset} disabled={resetTxt !== "RESET" || resetting} className="bg-red-600 hover:bg-red-700 text-white w-fit disabled:opacity-50" data-testid="reset-db-btn">{resetting ? "Svuotamento in corso..." : "Svuota database e ricrea admin"}</Button>
          </div>
        ) : (
          <div className="grid gap-3 max-w-md" data-testid="reset-result">
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">✓ Database svuotato. Nuovo admin creato{resetResult.email_inviata ? " e credenziali inviate via email." : " (invio email non riuscito, salva le credenziali qui sotto)."}</div>
            <div className="bg-[#FFF5EB] border border-[#F5D9B8] rounded-lg p-4 font-mono text-sm">
              <div>Email: <b>{resetResult.email}</b></div>
              <div>Password: <b>{resetResult.password}</b></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={scaricaResetPdf} data-testid="reset-pdf-btn">Scarica PDF</Button>
              <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(`Email: ${resetResult.email}\nPassword: ${resetResult.password}`); toast.success("Credenziali copiate");}} data-testid="reset-copy-btn">Copia</Button>
              <Button onClick={escLogout} className="bg-[#1F4E79] text-white" data-testid="reset-logout-btn">Ho salvato, esci</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 bg-white border-[#E2E8F0] mt-6" data-testid="inattivita-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#F7941E] flex items-center justify-center text-white"><KeyRound className="w-5 h-5"/></div>
          <div>
            <div className="font-bold text-stone-900">Disconnessione per inattività</div>
            <div className="text-sm text-stone-600">Dopo questi minuti senza attività appare un avviso e, dopo 1 minuto, l'utente viene disconnesso. Non avviene se è in videoconferenza.</div>
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label>Minuti di inattività</Label>
            <Input type="number" min="1" max="240" value={inattivita} onChange={e=>setInattivita(e.target.value)} className="w-40" data-testid="inattivita-input"/>
          </div>
          <Button disabled={savingInatt} onClick={async ()=>{ setSavingInatt(true); try { const { data } = await api.put("/settings/inattivita", { inactivity_min: +inattivita }); setInattivita(data.inactivity_min); toast.success("Impostazione salvata"); } catch { toast.error("Errore salvataggio"); } finally { setSavingInatt(false); } }} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="inattivita-save-btn">{savingInatt ? "Salvataggio..." : "Salva"}</Button>
        </div>
      </Card>

      {user?.role === "admin" && <LogAttivita/>}
      {user?.role === "admin" && <ChangelogAdmin/>}
    </div>
  );
}

function LogAttivita() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [loading, setLoading] = useState(false);
  const RES = { users:"Utenti", classi:"Classi", lezioni:"Lezioni", corsi:"Corsi", materiali:"Materiali", test:"Test", "registro-pagamenti":"Pagamenti", "piani-rate":"Piani rate", solleciti:"Solleciti", settings:"Impostazioni", iscrizioni:"Iscrizioni", materie:"Materie", "corsi-pubblici":"Corsi pubblici", auth:"Autenticazione" };
  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (dal) p.set("dal", dal);
      if (al) p.set("al", al);
      const { data } = await api.get(`/audit?${p.toString()}`);
      setRows(data);
    }
    catch { toast.error("Errore caricamento log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const azione = (r) => {
    if (r.method === "LOGIN") return "Accesso";
    const seg = (r.path || "").replace(/^\/api\//, "").split("/")[0];
    const res = RES[seg] || seg;
    const verbo = { POST:"Creazione", PUT:"Modifica", PATCH:"Modifica", DELETE:"Eliminazione" }[r.method] || r.method;
    return `${verbo} · ${res}`;
  };
  return (
    <Card className="p-6 bg-white border-[#E2E8F0] mt-6" data-testid="log-attivita-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#1F4E79] flex items-center justify-center text-white"><SlidersHorizontal className="w-5 h-5"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-bold text-stone-900">Log attività</div>
          <div className="text-sm text-stone-600">Accessi, creazioni, modifiche ed eliminazioni effettuate dagli utenti.</div>
        </div>
        <Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") load(); }} placeholder="Filtra per email o sezione" className="w-56" data-testid="log-filtro-input"/>
        <Input type="date" value={dal} onChange={e=>setDal(e.target.value)} className="w-40" title="Dal" data-testid="log-dal-input"/>
        <Input type="date" value={al} onChange={e=>setAl(e.target.value)} className="w-40" title="Al" data-testid="log-al-input"/>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="log-refresh-btn">{loading ? "..." : "Aggiorna"}</Button>
      </div>
      <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[150px_1fr_1fr] bg-[#F8FAFC] text-xs font-semibold text-stone-600 px-3 py-2">
          <div>Data/ora</div><div>Utente</div><div>Azione</div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-[#F1F5F9]">
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-[150px_1fr_1fr] px-3 py-2 text-sm" data-testid={`log-row-${r.id}`}>
              <div className="text-stone-500 font-mono text-xs">{r.ts ? new Date(r.ts).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
              <div className="truncate">{r.user_email}{r.role ? <span className="text-stone-400 text-xs"> · {r.role}</span> : ""}</div>
              <div>{azione(r)}</div>
            </div>
          ))}
          {rows.length === 0 && <div className="px-3 py-6 text-center text-sm text-stone-500">Nessuna attività registrata.</div>}
        </div>
      </div>
    </Card>
  );
}

function ChangelogAdmin() {
  const [version, setVersion] = useState("");
  const [entries, setEntries] = useState([]);
  const [versione, setVersione] = useState("");
  const [data, setData] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const r = await api.get("/changelog"); setVersion(r.data.version); setEntries(r.data.entries); }
    catch { toast.error("Errore caricamento modifiche"); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!descrizione.trim()) { toast.error("Inserisci una descrizione"); return; }
    setSaving(true);
    try { await api.post("/changelog", { versione, data, descrizione }); setVersione(""); setData(""); setDescrizione(""); toast.success("Modifica registrata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/changelog/${id}`); load(); } catch { toast.error("Errore eliminazione"); } };
  const scaricaPdf = async () => {
    try {
      const r = await api.get("/changelog/pdf", { responseType: "blob" });
      const link = document.createElement("a"); link.href = URL.createObjectURL(r.data); link.download = "changelog_isteform.pdf"; link.click();
    } catch { toast.error("Errore download changelog"); }
  };
  return (
    <Card className="p-6 bg-white border-[#E2E8F0] mt-6" data-testid="changelog-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#1F4E79] flex items-center justify-center text-white"><KeyRound className="w-5 h-5"/></div>
        <div className="flex-1">
          <div className="font-bold text-stone-900">Versione software & modifiche</div>
          <div className="text-sm text-stone-600 flex items-center gap-2 flex-wrap">Versione attuale: <Badge className="bg-[#1F4E79]">{version || "-"}</Badge> · Registro delle modifiche (visibile solo all'amministratore).</div>
        </div>
        <Button variant="outline" size="sm" onClick={scaricaPdf} data-testid="changelog-pdf-btn"><Download className="w-4 h-4 mr-2"/>Scarica changelog</Button>
      </div>
      <div className="grid gap-2 md:grid-cols-[140px_150px_1fr_auto] items-end mb-4">
        <div><Label className="text-xs">Versione</Label><Input value={versione} onChange={e=>setVersione(e.target.value)} placeholder="es. 2.1.0" data-testid="changelog-versione"/></div>
        <div><Label className="text-xs">Data</Label><Input type="date" value={data} onChange={e=>setData(e.target.value)} data-testid="changelog-data"/></div>
        <div><Label className="text-xs">Descrizione modifica</Label><Input value={descrizione} onChange={e=>setDescrizione(e.target.value)} placeholder="Cosa è stato modificato" data-testid="changelog-descrizione"/></div>
        <Button onClick={add} disabled={saving} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="changelog-add-btn">{saving ? "..." : "Aggiungi"}</Button>
      </div>
      <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#F1F5F9] max-h-72 overflow-y-auto">
        {entries.map(e => (
          <div key={e.id} className="flex items-start gap-3 px-3 py-2 text-sm" data-testid={`changelog-row-${e.id}`}>
            <div className="font-mono text-xs text-stone-500 w-24 shrink-0">{(e.data||"").slice(0,10)}</div>
            {e.versione && <Badge variant="outline" className="shrink-0">{e.versione}</Badge>}
            <div className="flex-1">{e.descrizione}</div>
            <button onClick={()=>del(e.id)} className="text-red-500 text-xs shrink-0" data-testid={`changelog-del-${e.id}`}>Elimina</button>
          </div>
        ))}
        {entries.length === 0 && <div className="px-3 py-6 text-center text-sm text-stone-500">Nessuna modifica registrata.</div>}
      </div>
    </Card>
  );
}
